import {
  CircleDashedIcon,
  EarthIcon,
  DiameterIcon,
  LandPlotIcon,
  MapPinnedIcon,
  MessageCircleQuestionIcon,
  RulerIcon,
  RulerDimensionLineIcon,
  ScalingIcon,
  ScissorsIcon,
  ShovelIcon,
  ShrubIcon,
  TreesIcon,
} from "lucide-react";
import { produce } from "immer";

import { getText, sentenceCase } from "shared/lib";

export const TreeModuleEventTypes = {
  AddRemovingTree: "AddRemovingTree",
  AddPruningTree: "AddPruningTree",
  AddDiggingTree: "AddDiggingTree",
};

export const TreePropertyType = {
  measurement_type: "measurement_type",
  species: "species",
  trunk_type: "trunk_type",
  dbh: "dbh",
  circumference: "circumference",
  height: "height",
  location: "location",
  reason: "reason",
  lop_prune_percentage: "lop_prune_percentage",
  distance: "distance",
  excavation: "excavation",
  size: "size",
  remove_or_prune: "remove_or_prune",
  pruning_quantity: "pruning_quantity",
  property_boundary: "property_boundary",
  canopy_size: "canopy_size",
  ground_level_alteration: "ground_level_alteration",
} as const;
export type TreePropertyType =
  (typeof TreePropertyType)[keyof typeof TreePropertyType];

export const TreePropertyIcon = {
  [TreePropertyType.species]: EarthIcon,
  [TreePropertyType.trunk_type]: TreesIcon,
  [TreePropertyType.dbh]: DiameterIcon,
  [TreePropertyType.circumference]: CircleDashedIcon,
  [TreePropertyType.height]: RulerDimensionLineIcon,
  [TreePropertyType.location]: MapPinnedIcon,
  [TreePropertyType.reason]: MessageCircleQuestionIcon,
  [TreePropertyType.lop_prune_percentage]: ShrubIcon,
  [TreePropertyType.distance]: RulerIcon,
  [TreePropertyType.excavation]: ShovelIcon,
  [TreePropertyType.size]: RulerDimensionLineIcon,
  [TreePropertyType.remove_or_prune]: ScissorsIcon,
  [TreePropertyType.pruning_quantity]: ScalingIcon,
  [TreePropertyType.property_boundary]: LandPlotIcon,
} as const;

export type BaseTreePropertyDescriptor = {
  placeholder: string;
  propertyType: TreePropertyType;
  value?: string;
  tooltip?: string;
};

export interface MeasurementTypeTreePropertyDescriptor<
  T extends TreePropertyType = typeof TreePropertyType.measurement_type,
> extends BaseTreePropertyDescriptor {
  propertyType: T;
  value?: "estimate" | "detailed" | "";
}

export interface SpeciesTreePropertyDescriptor<
  T extends TreePropertyType = typeof TreePropertyType.species,
> extends BaseTreePropertyDescriptor {
  propertyType: T;
  value?: "native" | "exotic" | "unsure" | "non-native" | "";
}

export interface TrunkTypeTreePropertyDescriptor<
  T extends TreePropertyType = typeof TreePropertyType.trunk_type,
> extends BaseTreePropertyDescriptor {
  propertyType: T;
  value?: "single" | "multi" | "vegetation" | "";
}

export interface DBHTreePropertyDescriptor extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.dbh;
  distanceAboveGround: string;
}

export interface CircumferenceTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.circumference;
  groundLevelCircumference: string;
  distanceAboveGround: string;
}

export interface HeightTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.height;
}

export interface LocationPropertyDescriptor extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.location;
  value?: "on site" | "off site" | "";
  description: string;
}

export interface ReasonTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.reason;
}

export interface LopPrunePercentageTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.lop_prune_percentage;
}

export interface DistanceTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.distance;
}

export interface ExcavationTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.excavation;
  value?: "yes" | "no" | "unsure" | "";
}

export interface SizeEstimateTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.size;
  value?: "small" | "medium" | "large" | "vegetation" | "";
}

export interface RemoveOrPruneTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.remove_or_prune;
  value?: "remove" | "prune" | "";
}

export interface PruningQuantityTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.pruning_quantity;
  value?: "minor" | "moderate" | "significant" | "";
}

export interface PropertyBoundaryTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.property_boundary;
  value?: "yes" | "no" | "unsure" | "";
}

export interface CanopySizeTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.canopy_size;
  value?: "yes" | "no" | "";
}

export interface GroundLevelAlterationTreePropertyDescriptor
  extends BaseTreePropertyDescriptor {
  propertyType: typeof TreePropertyType.ground_level_alteration;
  value?: "yes" | "no" | "unsure" | "";
}

export type TreePropertyDescriptor =
  | MeasurementTypeTreePropertyDescriptor
  | SpeciesTreePropertyDescriptor
  | TrunkTypeTreePropertyDescriptor
  | DBHTreePropertyDescriptor
  | CircumferenceTreePropertyDescriptor
  | HeightTreePropertyDescriptor
  | LocationPropertyDescriptor
  | ReasonTreePropertyDescriptor
  | LopPrunePercentageTreePropertyDescriptor
  | DistanceTreePropertyDescriptor
  | ExcavationTreePropertyDescriptor
  | SizeEstimateTreePropertyDescriptor
  | RemoveOrPruneTreePropertyDescriptor
  | PruningQuantityTreePropertyDescriptor
  | PropertyBoundaryTreePropertyDescriptor
  | CanopySizeTreePropertyDescriptor
  | GroundLevelAlterationTreePropertyDescriptor;

export type TreeDescriptor = {
  properties: Map<TreePropertyType, TreePropertyDescriptor>;
  title: string;
  treeId: string;
};

export function updateTreeProperty<T extends TreePropertyDescriptor>(
  treeDescriptor: TreeDescriptor,
  propertyType: TreePropertyType,
  updater: (draft: T) => void,
): TreeDescriptor {
  return produce(treeDescriptor, (draft) => {
    const property = draft.properties.get(propertyType) as T | undefined;

    if (property) {
      updater(property);
    }
  });
}

export function treeName(treeDescriptor: TreeDescriptor) {
  const segments: Array<string> = [];

  const sizeProperty = treeDescriptor.properties.get(TreePropertyType.size) as
    | SizeEstimateTreePropertyDescriptor
    | undefined;

  const trunkTypeProperty = treeDescriptor.properties.get(
    TreePropertyType.trunk_type,
  ) as TrunkTypeTreePropertyDescriptor | undefined;

  const speciesProperty = treeDescriptor.properties.get(
    TreePropertyType.species,
  ) as SpeciesTreePropertyDescriptor | undefined;

  const dbhProperty = treeDescriptor.properties.get(TreePropertyType.dbh) as
    | DBHTreePropertyDescriptor
    | undefined;

  const circumferenceProperty = treeDescriptor.properties.get(
    TreePropertyType.circumference,
  ) as CircumferenceTreePropertyDescriptor | undefined;

  const measurementTypeProperty = treeDescriptor.properties.get(
    TreePropertyType.measurement_type,
  ) as MeasurementTypeTreePropertyDescriptor | undefined;

  if (measurementTypeProperty?.value === "estimate") {
    if (
      sizeProperty?.value &&
      sizeProperty.value !== "vegetation" &&
      trunkTypeProperty?.value !== "vegetation"
    ) {
      segments.push(getText(sizeProperty.value));
    }
  } else {
    // Detailed measurements
    if (
      sizeProperty?.value !== "vegetation" &&
      trunkTypeProperty?.value !== "vegetation" &&
      dbhProperty?.value
    ) {
      const dbh = parseFloat(dbhProperty.value);
      if (dbh > 0 && dbh <= 0.3) {
        segments.push(getText("small"));
      } else if (dbh >= 0.4 && dbh <= 0.6) {
        segments.push(getText("medium"));
      } else if (dbh >= 0.7) {
        segments.push(getText("large"));
      }
    } else if (
      trunkTypeProperty?.value !== "vegetation" &&
      circumferenceProperty?.value
    ) {
      const circumference = parseFloat(circumferenceProperty.value);
      if (circumference > 0 && circumference <= 0.3) {
        segments.push(getText("small"));
      } else if (circumference >= 0.4 && circumference <= 1.9) {
        segments.push(getText("medium"));
      } else if (circumference >= 2.0) {
        segments.push(getText("large"));
      }
    }
  }

  if (speciesProperty?.value) {
    const species = speciesProperty.value;
    if (species === "native") {
      segments.push(getText("native"));
    } else if (species === getText("exotic")) {
      segments.push(getText("exotic"));
    }
  }

  if (
    sizeProperty?.value !== "vegetation" &&
    trunkTypeProperty?.value !== "vegetation" &&
    trunkTypeProperty?.value
  ) {
    const trunkType = trunkTypeProperty.value;
    if (trunkType === "single") {
      segments.push(getText("single stem"));
    } else if (trunkType === "multi") {
      segments.push(getText("multi stem"));
    }
  }

  if (
    sizeProperty?.value === "vegetation" ||
    trunkTypeProperty?.value === "vegetation"
  ) {
    segments.push(getText("vegetation"));
  } else {
    segments.push(getText("tree"));
  }

  segments.push(treeDescriptor.title);

  return sentenceCase(segments.join(" "));
}

export type NewRemovingTreeDescriptorOptions = {
  title: string;
  getText: typeof getText;
};

export type NewRemovingTreeDescriptorExtraOptions = {
  useCircumference?: boolean;
  distanceAboveGroundLevel?: string;
  showTreeHeight?: boolean;
  treeHeight?: string;
};

export function newRemovingTreeDescriptor(
  { title, getText }: NewRemovingTreeDescriptorOptions,
  options?: NewRemovingTreeDescriptorExtraOptions,
): TreeDescriptor {
  const properties = new Map<TreePropertyType, TreePropertyDescriptor>();

  properties.set(TreePropertyType.species, {
    placeholder: getText("Species"),
    propertyType: TreePropertyType.species,
    value: "",
  });
  properties.set(TreePropertyType.trunk_type, {
    placeholder: getText("Trunk type"),
    propertyType: TreePropertyType.trunk_type,
    value: "",
  });

  if (options?.useCircumference) {
    properties.set(TreePropertyType.circumference, {
      placeholder: getText("Circumference"),
      propertyType: TreePropertyType.circumference,
      value: "0.7",
      groundLevelCircumference: "0.7",
      distanceAboveGround: options?.distanceAboveGroundLevel || "1.4m",
    });
  } else {
    properties.set(TreePropertyType.dbh, {
      placeholder: getText("Size"),
      propertyType: TreePropertyType.dbh,
      value: "0.4",
      distanceAboveGround: options?.distanceAboveGroundLevel || "1.4m",
    });
  }

  if (options?.showTreeHeight) {
    properties.set(TreePropertyType.height, {
      placeholder: getText("Height"),
      propertyType: TreePropertyType.height,
      value: "",
    });
  }
  properties.set(TreePropertyType.location, {
    placeholder: getText("Location"),
    propertyType: TreePropertyType.location,
    value: "",
    description: "",
  });
  properties.set(TreePropertyType.reason, {
    placeholder: getText("Reason"),
    propertyType: TreePropertyType.reason,
    value: "",
  });

  return {
    properties,
    title,
    treeId: crypto.randomUUID(),
  };
}

export type NewPruningTreeDescriptorOptions = {
  title: string;
  getText: typeof getText;
};

export type NewPruningTreeDescriptorExtraOptions = {
  useCircumference?: boolean;
  distanceAboveGroundLevel?: string;
};

export function newPruningTreeDescriptor(
  { title, getText }: NewPruningTreeDescriptorOptions,
  options?: NewPruningTreeDescriptorExtraOptions,
): TreeDescriptor {
  const properties = new Map<TreePropertyType, TreePropertyDescriptor>();

  properties.set(TreePropertyType.species, {
    placeholder: getText("Species"),
    propertyType: TreePropertyType.species,
    value: "",
  });
  properties.set(TreePropertyType.trunk_type, {
    placeholder: getText("Trunk type"),
    propertyType: TreePropertyType.trunk_type,
    value: "",
  });
  if (options?.useCircumference) {
    properties.set(TreePropertyType.circumference, {
      placeholder: getText("Circumference"),
      propertyType: TreePropertyType.circumference,
      value: "0.7",
      groundLevelCircumference: "0.7",
      distanceAboveGround: options?.distanceAboveGroundLevel || "1.4m",
    });
  } else {
    properties.set(TreePropertyType.dbh, {
      placeholder: getText("Size"),
      propertyType: TreePropertyType.dbh,
      value: "0.4",
      distanceAboveGround: options?.distanceAboveGroundLevel || "1.4m",
    });
  }
  properties.set(TreePropertyType.location, {
    placeholder: getText("Location"),
    propertyType: TreePropertyType.location,
    value: "",
    description: "",
  });
  properties.set(TreePropertyType.lop_prune_percentage, {
    placeholder: getText("% prune"),
    propertyType: TreePropertyType.lop_prune_percentage,
    value: "",
  });
  properties.set(TreePropertyType.reason, {
    placeholder: getText("Reason"),
    propertyType: TreePropertyType.reason,
    value: "",
  });

  return {
    properties,
    title,
    treeId: crypto.randomUUID(),
  };
}

export type NewDiggingTreeDescriptorOptions = {
  title: string;
  getText: typeof getText;
};

export type NewDiggingTreeDescriptorExtraOptions = {
  useCircumference?: boolean;
  distanceAboveGroundLevel?: string;
  showTreeHeight?: boolean;
  treeHeight?: string;
};

export function newDiggingTreeDescriptor(
  { title, getText }: NewDiggingTreeDescriptorOptions,
  options?: NewDiggingTreeDescriptorExtraOptions,
): TreeDescriptor {
  const properties = new Map<TreePropertyType, TreePropertyDescriptor>();

  properties.set(TreePropertyType.species, {
    placeholder: getText("Species"),
    propertyType: TreePropertyType.species,
    value: "",
  });
  properties.set(TreePropertyType.trunk_type, {
    placeholder: getText("Trunk type"),
    propertyType: TreePropertyType.trunk_type,
    value: "",
  });
  if (options?.useCircumference) {
    properties.set(TreePropertyType.circumference, {
      placeholder: getText("Circumference"),
      propertyType: TreePropertyType.circumference,
      value: "0.7",
      groundLevelCircumference: "0.7",
      distanceAboveGround: options?.distanceAboveGroundLevel || "1.4m",
    });
  } else {
    properties.set(TreePropertyType.dbh, {
      placeholder: getText("Size"),
      propertyType: TreePropertyType.dbh,
      value: "0.4",
      distanceAboveGround: options?.distanceAboveGroundLevel || "1.4m",
    });
  }
  if (options?.showTreeHeight) {
    properties.set(TreePropertyType.height, {
      placeholder: getText("Height"),
      propertyType: TreePropertyType.height,
      value: "",
    });
  }
  properties.set(TreePropertyType.location, {
    placeholder: getText("Location"),
    propertyType: TreePropertyType.location,
    value: "",
    description: "",
  });
  properties.set(TreePropertyType.distance, {
    placeholder: getText("Distance"),
    propertyType: TreePropertyType.distance,
    value: "2",
  });
  properties.set(TreePropertyType.excavation, {
    placeholder: getText("Excavation"),
    propertyType: TreePropertyType.excavation,
    value: "",
  });

  return {
    properties,
    title,
    treeId: crypto.randomUUID(),
  };
}

export type NewNearbyTreeDescriptorOptions = {
  title: string;
  getText: typeof getText;
};

export type NewNearbyTreeDescriptorExtraOptions = {
  distanceAboveGroundLevel?: string;
  useCircumference?: boolean;
};

export function newNearbyTreeDescriptor(
  { title, getText }: NewNearbyTreeDescriptorOptions,
  options?: NewNearbyTreeDescriptorExtraOptions,
): TreeDescriptor {
  const properties = new Map<TreePropertyType, TreePropertyDescriptor>();

  properties.set(TreePropertyType.measurement_type, {
    placeholder: getText("measurement type"),
    propertyType: TreePropertyType.measurement_type,
  });
  properties.set(TreePropertyType.size, {
    placeholder: getText("Size"),
    propertyType: TreePropertyType.size,
    tooltip: getText("Size estimate"),
  });
  properties.set(TreePropertyType.trunk_type, {
    placeholder: getText("Trunk type"),
    propertyType: TreePropertyType.trunk_type,
    tooltip: getText("Trunk type"),
  });
  if (options?.useCircumference) {
    properties.set(TreePropertyType.circumference, {
      placeholder: getText("Circumference"),
      propertyType: TreePropertyType.circumference,
      groundLevelCircumference: "0.7",
      distanceAboveGround: options?.distanceAboveGroundLevel || "1.4m",
      tooltip: getText("Circumference measurement"),
    });
  } else {
    properties.set(TreePropertyType.dbh, {
      placeholder: getText("Diameter"),
      propertyType: TreePropertyType.dbh,
      distanceAboveGround: options?.distanceAboveGroundLevel || "1.4m",
      tooltip: getText("DBH measurement"),
    });
  }
  properties.set(TreePropertyType.distance, {
    placeholder: getText("Distance"),
    propertyType: TreePropertyType.distance,
    tooltip: getText("Distance"),
  });
  properties.set(TreePropertyType.species, {
    placeholder: getText("Species"),
    propertyType: TreePropertyType.species,
    tooltip: getText("Species"),
  });
  properties.set(TreePropertyType.ground_level_alteration, {
    placeholder: getText("Ground level alteration"),
    propertyType: TreePropertyType.ground_level_alteration,
    tooltip: getText(
      "Will the proposed works near this tree alter the ground level?",
    ),
  });

  return {
    properties,
    title,
    treeId: crypto.randomUUID(),
  };
}

export type NewRemovingOrPruningTreeDescriptorOptions = {
  title: string;
  getText: typeof getText;
};

export type NewRemovingOrPruningTreeDescriptorExtraOptions = {
  distanceAboveGroundLevel?: string;
  useCircumference?: boolean;
  showTreeHeight?: boolean;
  treeHeight?: string;
};

export function newRemovingOrPruningTreeDescriptor(
  { title, getText }: NewRemovingOrPruningTreeDescriptorOptions,
  options?: NewRemovingOrPruningTreeDescriptorExtraOptions,
): TreeDescriptor {
  const properties = new Map<TreePropertyType, TreePropertyDescriptor>();

  properties.set(TreePropertyType.measurement_type, {
    placeholder: getText("measurement type"),
    propertyType: TreePropertyType.measurement_type,
  });
  properties.set(TreePropertyType.size, {
    placeholder: getText("Size"),
    propertyType: TreePropertyType.size,
    tooltip: getText("Size estimate"),
  });
  properties.set(TreePropertyType.property_boundary, {
    placeholder: getText("Property boundary"),
    propertyType: TreePropertyType.property_boundary,
    tooltip: getText(
      "Is the tree within 6 metres of the property's front boundary, or within 4.5 metres of its rear boundary?",
    ),
  });
  properties.set(TreePropertyType.trunk_type, {
    placeholder: getText("Trunk type"),
    propertyType: TreePropertyType.trunk_type,
    tooltip: getText("Trunk type"),
  });
  if (options?.useCircumference) {
    properties.set(TreePropertyType.circumference, {
      placeholder: getText("Circumference"),
      propertyType: TreePropertyType.circumference,
      groundLevelCircumference: "0.7",
      distanceAboveGround: options?.distanceAboveGroundLevel || "1.4m",
      tooltip: getText("Circumference measurement"),
    });
  } else {
    properties.set(TreePropertyType.dbh, {
      placeholder: getText("Diameter"),
      propertyType: TreePropertyType.dbh,
      distanceAboveGround: options?.distanceAboveGroundLevel || "1.4m",
      tooltip: getText("DBH measurement"),
    });
  }
  if (options?.showTreeHeight) {
    properties.set(TreePropertyType.height, {
      placeholder: getText("Height"),
      propertyType: TreePropertyType.height,
      tooltip: getText("Height"),
    });
  }
  properties.set(TreePropertyType.canopy_size, {
    placeholder: getText("Canopy size"),
    propertyType: TreePropertyType.canopy_size,
    tooltip: getText("Canopy size"),
  });
  properties.set(TreePropertyType.species, {
    placeholder: getText("Species"),
    propertyType: TreePropertyType.species,
    tooltip: getText("Species"),
  });
  properties.set(TreePropertyType.remove_or_prune, {
    placeholder: getText("What"),
    propertyType: TreePropertyType.remove_or_prune,
    tooltip: getText("Remove or prune?"),
  });
  properties.set(TreePropertyType.pruning_quantity, {
    placeholder: getText("Quantity"),
    propertyType: TreePropertyType.pruning_quantity,
    tooltip: getText("Pruning quantity"),
  });
  properties.set(TreePropertyType.reason, {
    placeholder: getText("Reason"),
    propertyType: TreePropertyType.reason,
    tooltip: getText("Reason"),
  });

  return {
    properties,
    title,
    treeId: crypto.randomUUID(),
  };
}
